import React, { useEffect, useRef, useState } from "react";
import { createAddressEvent, formatBookingJson, getFormDataForStorage, manualGoogleEvents } from '../helpers/helpers'
import { GooglePlaceAutocomplete, SelectInput } from '@hometown/react-hometown-lib';
import CircularProgress from '@mui/material/CircularProgress';

const IconHome = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
    </svg>
);

const IconWork = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
    </svg>
);

const options = [
    {
      "value": "1",
      "label": "Trash"
    },
    {
      "value": "2",
      "label": "Construction Debris"
    },
    {
      "value": "3",
      "label": "Concrete"
    },
    {
      "value": "4",
      "label": "Roofing"
    },
    {
      "value": "5",
      "label": "Yard Waste"
    },
    {
      "value": "6",
      "label": "Dirt"
    },
    {
      "value": "7",
      "label": "Rock or Brick"
    }
]

const ViewDumpsterPricesForm = ({ state, btnLabel, onSubmit, id }) => {
    const dataFromStorage = getFormDataForStorage(); 
    let addressData = dataFromStorage?.addressData ? { ...dataFromStorage.addressData } : {};
    let defaultWasteType = dataFromStorage?.wasteType || '';
    const [googlePlace, setGooglePlace] = useState('');
    const [addressError, setAddressError] = useState(false);
    const [wasteError, setWasteError] = useState(false);
    const [userTypeError, setUserTypeError] = useState(false);
    const [selectedWasteType, setSelectedWasteType] = useState(defaultWasteType);
    const [formValues, setFormValues] = useState(addressData ? { ...addressData } : {});
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Resolved in background when user selects address, passed to formatBookingJson on submit
    const resolvedPlaceIdRef = useRef(null);
    // 09/09: no longer defaults to 'homeowner' -- per design review, forcing an explicit choice
    // is worth the extra click for homeowners because it stops contractors from being silently
    // lumped in with the majority segment. Still respects a real prior choice from localStorage.
    const [userType, setUserType] = useState(null);
    // Focus targets for the "jump to first missing field" behavior on a failed submit -- per
    // Mark's design review, an active button that clearly points at what's missing beats a
    // disabled one that gives no explanation (Nielsen Norman / WCAG guidance he cited).
    const homeownerBtnRef = useRef(null);

    useEffect(() => {
        const stored = localStorage.getItem('userType');
        if (stored) setUserType(stored);
    }, []);

    useEffect(() => {
      const element = document.getElementById("waste-type-select");
      if (!element) return;

      if (selectedWasteType) {
        element.classList.add("select-shrink");
      } else {
        element.classList.remove("select-shrink");
      }
    }, [selectedWasteType]);

    const handleSubmit = async () => {
      const missingAddress = !formValues.odf_postal_code;
      const missingWaste = !selectedWasteType;
      const missingUserType = !userType;

      setAddressError(missingAddress);
      setWasteError(missingWaste);
      setUserTypeError(missingUserType);

      if (missingAddress || missingWaste || missingUserType) {
        // Per Mark's design review (citing Nielsen Norman/WCAG): the button stays active and
        // clickable the whole time -- graying it out until the form is complete gives no
        // explanation of what's missing and often fails contrast/screen-reader requirements.
        // Jumping focus to the first missing field on a failed attempt gives that explanation
        // instead.
        if (missingAddress) {
          document.getElementById('view-prices-address')?.focus();
        } else if (missingWaste) {
          document.getElementById(id)?.focus();
        } else if (missingUserType) {
          homeownerBtnRef.current?.focus();
        }
        return;
      }

      const formattedValues = formatBookingJson({ ...formValues, wasteType: selectedWasteType }, resolvedPlaceIdRef.current);
      try {
        setIsSubmitting(true);
        localStorage.setItem('userType', userType);
        if(onSubmit) {
          await onSubmit(formattedValues);
        } else {
          manualGoogleEvents('manualFormSubmition');
          window.location.href = '/book';
        }
      } catch (error) {
        console.log('error', error)
      } finally {
        setIsSubmitting(false);
      }
    }

    const handleUserTypeChange = (type) => {
      setUserType(type);
      // Cleared the instant they pick one -- per design review, error states clear on
      // interaction with that specific field, not on the next submit attempt.
      setUserTypeError(false);
      localStorage.setItem('userType', type);
    };

    const handleWasteChange = (wasteType) => {
      if(wasteType){
        setWasteError(false);
        setSelectedWasteType(wasteType)
      } else {
        setWasteError(true)
      }
    }

    const handleAddressChange = (place) => {
      setGooglePlace(JSON.stringify(place))
      setAddressError(false);
      let addressValues = {}
      place.map((value) => {
          addressValues["odf_" + value[0]] = value[1]
      })
      createAddressEvent({
        street1: addressValues.odf_street,
        street2: addressValues.odf_street2,
        city: addressValues.odf_locality,
        zipCode: addressValues.odf_postal_code,
        state: addressValues.odf_state
      })
      setFormValues({ ...addressValues })


      // Resolve placeId in background so it's ready when user hits submit
      resolvedPlaceIdRef.current = null;
      const zip = addressValues.odf_postal_code;
      const city = addressValues.odf_locality;
      if (zip && city) {
        fetch(
          `${process.env.GATSBY_DRUPAL_API_MANAGMENT}/place-by-zip-and-name/${zip}/${city}`,
          { headers: { 'Ocp-Apim-Subscription-Key': '30ca1663de42420bb4a9894d1898e91e' } }
        )
        .then(res => res.json())
        .then(data => { if (data?.id) resolvedPlaceIdRef.current = +data.id; })
        .catch(() => {});
      }
    }

    return (
        <div className="prices-form">
          {state &&
            <div class="prices-title">
                <h3>View dumpsters for rent in {state}</h3>
            </div>
            }
            <div className="address-ondeff">
              <GooglePlaceAutocomplete
                  id='view-prices-address'
                  name='address'
                  label="Enter delivery address"
                  fieldName="address"
                  placeholder=""
                  apiKey={`${process.env.GATSBY_BOOKABLE_API_AUTOCOMPLETE}`}
                  className="fill-available"
                  resetError = {()=>{}}
                  onResetField = {()=>{setGooglePlace('')}}
                  onChange = {(place) => handleAddressChange(place)}
                  onChangeValue={googlePlace}
                  formValues={formValues}
                  fullWidth
                  alwaysShrink={!!formValues?.odf_postal_code}
                  error={addressError}
                  helperText={addressError && 'Please enter a valid delivery address.'}
                  // showSearchIcon
              />
            </div>
            <div id="waste-type-select" className="select-ondeff outlined-select">
              <SelectInput
                  id={id}
                  className="outlined-select"
                  value={selectedWasteType}
                  name="wasteType"
                  label="Select your waste"
                  data={options}
                  onChange={handleWasteChange}
                  error="Please select a waste type from the list."
                  isError={wasteError}
                  fullWidth
              />
            </div>
            <div className="prices-form__user-type-label">
              I'm a:
              {!userType && <span className="prices-form__user-type-hint">Selection required</span>}
            </div>
            <div className={`prices-form__user-type${userTypeError ? ' has-error' : ''}`}>
                <button
                    ref={homeownerBtnRef}
                    className={`prices-form__user-type-btn${userType === 'homeowner' ? ' active' : ''}`}
                    onClick={() => handleUserTypeChange('homeowner')}
                    type="button"
                >
                    <IconHome /> Homeowner
                </button>
                <button
                    className={`prices-form__user-type-btn${userType === 'business' ? ' active' : ''}`}
                    onClick={() => handleUserTypeChange('business')}
                    type="button"
                >
                    <IconWork /> Contractor/Business
                </button>
            </div>
            {userTypeError &&
              <div className="prices-form__user-type-error">Please select whether you are a Homeowner or Contractor/Business.</div>
            }
            <div className="button-wrapper">
              <button class='view-dumpster-prices' onClick={handleSubmit}>{isSubmitting ? <CircularProgress style={{ color: '#FFF' }} /> : btnLabel}</button>
            </div>
        </div>
    )
}

export default ViewDumpsterPricesForm;
